// src/rms/utils/otherFactorsSurfacing.js
//
// Auto-surfacing for the ADHD triage decision "Other Factors" step (TD-185).
//
// Each Step-2 factor (adhdTriageOutcomes.OTHER_FACTOR_GROUPS) has a confirmed
// data source in the service-user forms — the mapping was verified against the
// org's live published schemas on TD-185 (Appendix B). This module holds that
// factorKey -> sources mapping and a pure resolver that turns the patient's
// submitted answers into human-readable rows the proforma renders in place of
// "Not captured — clinician to note".
//
// Design decisions (flagged for clinical review):
//  - Read-only surfacing of what the family/clinician already entered — no
//    interpretation. The clinician still records their own note on every factor.
//  - Checkbox sources with `options` set only surface the listed options when
//    selected (the positive evidence for that factor); a "Don't know / Not sure"
//    selection is surfaced as-is (never treated as missing). Free-text / radio
//    sources surface their answer whenever present.
//  - HFP-sourced items exist only for primary-school referrals; secondary
//    referrals skip the HFP, so an HFP source with no loaded schema simply
//    contributes nothing and the factor falls back to its non-HFP sources (or
//    to "Not captured" + note).
//  - Two rows are intentionally note-first per Appendix B: `cognitiveFunctioning`
//    (no direct measure — academic-progress/diagnoses surfaced as proxies) and
//    trauma *support* (only exposure is captured). The note box always remains.

// Roles whose instances we read. Referral forms are dual-respondent (school +
// guardian, the latter via lg_* question ids); the 7 triage forms are
// guardian-only.
const HFP_FORM_PREFIX = 'hfpV2';

/**
 * factorKey -> ordered list of sources.
 * Each source: { formKey, questionId, role, options? }
 *   - role: 'guardian' | 'school_user'
 *   - options: for checkbox questions, the option ids that evidence this factor;
 *     when omitted the whole answer is surfaced.
 */
export const ADHD_FACTOR_SOURCES = {
  // ── Medical & sensory ──────────────────────────────────────────────────────
  learningDisabilityCoOccurring: [
    { formKey: 'theirMedicalHistory', questionId: 'diagnosed_conditions', role: 'guardian' },
    { formKey: 'theirMentalHealthHistory', questionId: 'behavioural_neurodevelopmental', role: 'guardian', options: ['learning_disability'] },
    { formKey: 'educationHistory', questionId: 'diagnosed_with', role: 'school_user' },
    { formKey: 'educationHistory', questionId: 'lg_diagnosed_with', role: 'guardian' },
  ],
  medications: [
    { formKey: 'theirMedicalHistory', questionId: 'current_medications', role: 'guardian' },
    { formKey: 'theirMedicalHistory', questionId: 'supplements', role: 'guardian' },
  ],
  speechLanguageCommunication: [
    { formKey: 'theirMentalHealthHistory', questionId: 'behavioural_neurodevelopmental', role: 'guardian', options: ['speech_language'] },
    { formKey: 'requestForReferral', questionId: 'other_services', role: 'school_user', options: ['salt'] },
    { formKey: 'requestForReferral', questionId: 'lg_other_services', role: 'guardian', options: ['salt'] },
    // Primary-school only (HFP): full Speech, Language & Communication domain.
    { formKey: 'hfpV2SpeechLanguageCommunication', questionId: null, role: 'guardian', hfp: true },
  ],
  hearingOrVision: [
    { formKey: 'theirMedicalHistory', questionId: 'sight_hearing_concerns', role: 'guardian' },
    { formKey: 'theirMedicalHistory', questionId: 'hearing_issues', role: 'guardian' },
    { formKey: 'theirMedicalHistory', questionId: 'last_eye_test', role: 'guardian' },
    { formKey: 'theirMedicalHistory', questionId: 'wears_glasses', role: 'guardian' },
    { formKey: 'theirMedicalHistory', questionId: 'sight_hearing_referrals', role: 'guardian' },
  ],
  sensoryNeedBehaviour: [
    { formKey: 'theirMentalHealthHistory', questionId: 'behavioural_neurodevelopmental', role: 'guardian', options: ['spd'] },
    { formKey: 'reasonForReferral', questionId: 'led_to_referral', role: 'school_user', options: ['sensory'] },
    { formKey: 'hfpV2Sensory', questionId: null, role: 'guardian', hfp: true },
  ],
  // ── Developmental & cognitive ───────────────────────────────────────────────
  // No direct cognitive-functioning measure exists; surface academic progress +
  // learning-difference diagnoses as proxies (note-first per Appendix B).
  cognitiveFunctioning: [
    { formKey: 'educationHistory', questionId: 'academic_progress', role: 'school_user' },
    { formKey: 'educationHistory', questionId: 'lg_academic_progress', role: 'guardian' },
    { formKey: 'educationHistory', questionId: 'diagnosed_with', role: 'school_user' },
  ],
  developmentalDelay: [
    { formKey: 'theirEarlyLife', questionId: 'developmental_milestones', role: 'guardian' },
    { formKey: 'theirEarlyLife', questionId: 'slower_to_reach_skills', role: 'guardian' },
    { formKey: 'theirEarlyLife', questionId: 'lost_skills', role: 'guardian' },
    { formKey: 'theirMedicalHistory', questionId: 'diagnosed_conditions', role: 'guardian', options: ['developmental_delay'] },
  ],
  // ── Mental health & wellbeing ───────────────────────────────────────────────
  anxiety: [
    { formKey: 'theirMentalHealthHistory', questionId: 'mental_health_disorders', role: 'guardian', options: ['gad', 'social_anxiety', 'separation_anxiety'] },
    { formKey: 'theirMentalHealthHistory', questionId: 'current_support', role: 'guardian' },
  ],
  trauma: [
    { formKey: 'theirEarlyLife', questionId: 'aces', role: 'guardian', options: ['witnessed_domestic_violence', 'death_family_member', 'family_substance_mental_health', 'separation_from_parent'] },
    { formKey: 'theirEarlyLife', questionId: 'scary_upsetting_event', role: 'guardian' },
    { formKey: 'theirMentalHealthHistory', questionId: 'mental_health_disorders', role: 'guardian', options: ['ptsd'] },
  ],
  attachmentDisorderSymptoms: [
    { formKey: 'theirMentalHealthHistory', questionId: 'mental_health_disorders', role: 'guardian', options: ['attachment_disorder'] },
  ],
  // ── Family, home & lifestyle ────────────────────────────────────────────────
  boundariesParenting: [
    { formKey: 'theirFamilyAndHomeLife', questionId: 'support_used', role: 'guardian', options: ['parenting_programmes'] },
    { formKey: 'theirFamilyAndHomeLife', questionId: 'helpful_strategies', role: 'guardian' },
    { formKey: 'theirFamilyAndHomeLife', questionId: 'unhelpful_strategies', role: 'guardian' },
    { formKey: 'theirFamilyAndHomeLife', questionId: 'parental_coping', role: 'guardian' },
    { formKey: 'theirFamilyAndHomeLife', questionId: 'conflicts_at_home', role: 'guardian' },
  ],
  screenTimeGaming: [
    { formKey: 'theirLifestyle', questionId: 'daily_screen_time', role: 'guardian' },
    { formKey: 'theirLifestyle', questionId: 'screen_in_bedroom', role: 'guardian' },
    { formKey: 'theirLifestyle', questionId: 'screen_content', role: 'guardian' },
    { formKey: 'theirLifestyle', questionId: 'screen_challenges', role: 'guardian' },
    { formKey: 'theirLifestyle', questionId: 'in_app_purchases', role: 'guardian' },
    { formKey: 'theirLifestyle', questionId: 'gaming_concerns', role: 'guardian' },
  ],
  substanceMisuse: [
    { formKey: 'theirLifestyle', questionId: 'substance_concerns', role: 'guardian' },
    { formKey: 'theirFamilyHistory', questionId: 'family_addiction_history', role: 'guardian' },
  ],
  homeLivingConditions: [
    { formKey: 'theirFamilyAndHomeLife', questionId: 'ongoing_stresses', role: 'guardian' },
    { formKey: 'theirFamilyAndHomeLife', questionId: 'who_lives_at_home', role: 'guardian' },
    { formKey: 'theirFamilyAndHomeLife', questionId: 'housing_duration', role: 'guardian' },
    { formKey: 'theirEarlyLife', questionId: 'aces', role: 'guardian', options: ['accommodation_issues'] },
  ],
  // ── Support ─────────────────────────────────────────────────────────────────
  previousSupportAccessed: [
    { formKey: 'requestForReferral', questionId: 'other_services', role: 'school_user' },
    { formKey: 'requestForReferral', questionId: 'lg_other_services', role: 'guardian' },
    { formKey: 'educationHistory', questionId: 'support_in_place', role: 'school_user' },
    { formKey: 'educationHistory', questionId: 'lg_support_in_place', role: 'guardian' },
    { formKey: 'theirMentalHealthHistory', questionId: 'current_support', role: 'guardian' },
    { formKey: 'theirFamilyAndHomeLife', questionId: 'support_used', role: 'guardian' },
  ],
};

/**
 * factorKey -> sources for the autism triage decision wizard's factor rows
 * (AUTISM_OTHER_FACTOR_GROUPS). Reuses only source mappings verified on TD-185
 * (Appendix B); rows with no verified source yet — EHCP status, the
 * safeguarding step, school non-attendance and school moves — carry an empty
 * list and fall back to "Not captured — clinician to note" until the referral
 * form captures them.
 */
export const AUTISM_FACTOR_SOURCES = {
  // ── Medical, medication & development ───────────────────────────────────────
  medications: [
    { formKey: 'theirMedicalHistory', questionId: 'current_medications', role: 'guardian' },
    { formKey: 'theirMedicalHistory', questionId: 'supplements', role: 'guardian' },
  ],
  developmentalDelayHistory: [
    { formKey: 'theirEarlyLife', questionId: 'developmental_milestones', role: 'guardian' },
    { formKey: 'theirEarlyLife', questionId: 'slower_to_reach_skills', role: 'guardian' },
    { formKey: 'theirEarlyLife', questionId: 'lost_skills', role: 'guardian' },
    { formKey: 'theirMedicalHistory', questionId: 'diagnosed_conditions', role: 'guardian', options: ['developmental_delay'] },
  ],
  speechLanguageSeparate: [
    { formKey: 'theirMentalHealthHistory', questionId: 'behavioural_neurodevelopmental', role: 'guardian', options: ['speech_language'] },
    { formKey: 'requestForReferral', questionId: 'other_services', role: 'school_user', options: ['salt'] },
    { formKey: 'requestForReferral', questionId: 'lg_other_services', role: 'guardian', options: ['salt'] },
    { formKey: 'hfpV2SpeechLanguageCommunication', questionId: null, role: 'guardian', hfp: true },
  ],
  hearingOrVision: [
    { formKey: 'theirMedicalHistory', questionId: 'sight_hearing_concerns', role: 'guardian' },
    { formKey: 'theirMedicalHistory', questionId: 'hearing_issues', role: 'guardian' },
    { formKey: 'theirMedicalHistory', questionId: 'last_eye_test', role: 'guardian' },
    { formKey: 'theirMedicalHistory', questionId: 'wears_glasses', role: 'guardian' },
    { formKey: 'theirMedicalHistory', questionId: 'sight_hearing_referrals', role: 'guardian' },
  ],
  // ── Sensory ─────────────────────────────────────────────────────────────────
  sensoryNeedBehaviour: [
    { formKey: 'theirMentalHealthHistory', questionId: 'behavioural_neurodevelopmental', role: 'guardian', options: ['spd'] },
    { formKey: 'reasonForReferral', questionId: 'led_to_referral', role: 'school_user', options: ['sensory'] },
    { formKey: 'hfpV2Sensory', questionId: null, role: 'guardian', hfp: true },
  ],
  // ── Cognition & learning ────────────────────────────────────────────────────
  learningDisability: [
    { formKey: 'theirMedicalHistory', questionId: 'diagnosed_conditions', role: 'guardian' },
    { formKey: 'theirMentalHealthHistory', questionId: 'behavioural_neurodevelopmental', role: 'guardian', options: ['learning_disability'] },
    { formKey: 'educationHistory', questionId: 'diagnosed_with', role: 'school_user' },
    { formKey: 'educationHistory', questionId: 'lg_diagnosed_with', role: 'guardian' },
  ],
  specificLearningDifferences: [
    { formKey: 'educationHistory', questionId: 'diagnosed_with', role: 'school_user' },
    { formKey: 'educationHistory', questionId: 'lg_diagnosed_with', role: 'guardian' },
  ],
  ehcpStatus: [
    { formKey: 'educationHistory', questionId: 'support_in_place', role: 'school_user', options: ['ehcp'] },
    { formKey: 'educationHistory', questionId: 'lg_support_in_place', role: 'guardian', options: ['ehcp'] },
    { formKey: 'reasonForReferral', questionId: 'hopes_outcome', role: 'school_user', options: ['support_ehcp'] },
    { formKey: 'reasonForReferral', questionId: 'lg_desired_outcomes', role: 'guardian', options: ['support_ehcp'] },
  ],
  cognitiveFunctioning: [
    { formKey: 'educationHistory', questionId: 'academic_progress', role: 'school_user' },
    { formKey: 'educationHistory', questionId: 'lg_academic_progress', role: 'guardian' },
    { formKey: 'educationHistory', questionId: 'diagnosed_with', role: 'school_user' },
  ],
  // ── Mental health & wellbeing ───────────────────────────────────────────────
  mentalHealthDiagnosed: [
    { formKey: 'theirMentalHealthHistory', questionId: 'mental_health_disorders', role: 'guardian' },
    { formKey: 'theirMentalHealthHistory', questionId: 'current_support', role: 'guardian' },
  ],
  mentalWellbeingDifficulties: [
    { formKey: 'theirMentalHealthHistory', questionId: 'mental_health_disorders', role: 'guardian', options: ['gad', 'social_anxiety', 'separation_anxiety'] },
    { formKey: 'theirMentalHealthHistory', questionId: 'current_support', role: 'guardian' },
  ],
  adverseChildhoodExperiences: [
    { formKey: 'theirEarlyLife', questionId: 'aces', role: 'guardian' },
    { formKey: 'theirEarlyLife', questionId: 'scary_upsetting_event', role: 'guardian' },
    { formKey: 'theirMentalHealthHistory', questionId: 'mental_health_disorders', role: 'guardian', options: ['ptsd'] },
  ],
  // ── Safeguarding ────────────────────────────────────────────────────────────
  // Verified against the live dev schemas (2026-07-24). Meaningful negatives
  // ("No", "None of the above", "Not to my knowledge") are surfaced whole so
  // the clinician sees the recorded answer, not an absence. S47 history and
  // radicalisation risk are not asked anywhere in the current forms — those
  // rows stay note-only until the form questions are added.
  safeguardingActiveInvolvement: [
    { formKey: 'requestForReferral', questionId: 'lg_social_care_involved', role: 'guardian' },
    { formKey: 'requestForReferral', questionId: 'lg_care_arrangements', role: 'guardian', options: ['looked_after_child', 'adopted', 'sgo', 'care_order', 'legal_order', 'legal_proceedings', 'private_fostering', 'lives_with_family_friends', 'child_arrangement_order'] },
  ],
  safeguardingS47History: [],
  safeguardingPlans: [
    { formKey: 'requestForReferral', questionId: 'lg_social_care_plan', role: 'guardian' },
  ],
  safeguardingExploitationRisk: [
    { formKey: 'theirHistoryWithTheLaw', questionId: 'victim_of_harm', role: 'guardian' },
  ],
  safeguardingRadicalisationRisk: [],
  // ── Family, home & lifestyle ────────────────────────────────────────────────
  boundariesParenting: [
    { formKey: 'theirFamilyAndHomeLife', questionId: 'support_used', role: 'guardian', options: ['parenting_programmes'] },
    { formKey: 'theirFamilyAndHomeLife', questionId: 'helpful_strategies', role: 'guardian' },
    { formKey: 'theirFamilyAndHomeLife', questionId: 'unhelpful_strategies', role: 'guardian' },
    { formKey: 'theirFamilyAndHomeLife', questionId: 'parental_coping', role: 'guardian' },
    { formKey: 'theirFamilyAndHomeLife', questionId: 'conflicts_at_home', role: 'guardian' },
  ],
  screenTimeGaming: [
    { formKey: 'theirLifestyle', questionId: 'daily_screen_time', role: 'guardian' },
    { formKey: 'theirLifestyle', questionId: 'screen_in_bedroom', role: 'guardian' },
    { formKey: 'theirLifestyle', questionId: 'screen_content', role: 'guardian' },
    { formKey: 'theirLifestyle', questionId: 'screen_challenges', role: 'guardian' },
    { formKey: 'theirLifestyle', questionId: 'in_app_purchases', role: 'guardian' },
    { formKey: 'theirLifestyle', questionId: 'gaming_concerns', role: 'guardian' },
  ],
  substanceMisuse: [
    { formKey: 'theirLifestyle', questionId: 'substance_concerns', role: 'guardian' },
    { formKey: 'theirFamilyHistory', questionId: 'family_addiction_history', role: 'guardian' },
  ],
  homeLivingConditions: [
    { formKey: 'theirFamilyAndHomeLife', questionId: 'ongoing_stresses', role: 'guardian' },
    { formKey: 'theirFamilyAndHomeLife', questionId: 'who_lives_at_home', role: 'guardian' },
    { formKey: 'theirFamilyAndHomeLife', questionId: 'housing_duration', role: 'guardian' },
    { formKey: 'theirEarlyLife', questionId: 'aces', role: 'guardian', options: ['accommodation_issues'] },
  ],
  // ── Education & attendance ──────────────────────────────────────────────────
  // Verified against the live dev schemas (2026-07-24).
  schoolNonAttendance: [
    { formKey: 'educationHistory', questionId: 'attendance_frequency', role: 'school_user' },
    { formKey: 'educationHistory', questionId: 'absence_history', role: 'school_user' },
    { formKey: 'educationHistory', questionId: 'lg_current_education_status', role: 'guardian' },
    { formKey: 'reasonForReferral', questionId: 'led_to_referral', role: 'school_user', options: ['ebsa'] },
    { formKey: 'reasonForReferral', questionId: 'experiencing_following', role: 'school_user', options: ['school_refusal', 'persistent_avoidance'] },
    { formKey: 'reasonForReferral', questionId: 'lg_experiencing_following', role: 'guardian', options: ['school_refusal', 'persistent_avoidance'] },
  ],
  schoolMoves: [
    { formKey: 'educationHistory', questionId: 'lg_attended_settings', role: 'guardian', options: ['multiple_temp_placements', 'multiple_moves'] },
    { formKey: 'educationHistory', questionId: 'current_environment', role: 'school_user' },
  ],
  // ── Support ─────────────────────────────────────────────────────────────────
  previousSupportAccessed: [
    { formKey: 'requestForReferral', questionId: 'other_services', role: 'school_user' },
    { formKey: 'requestForReferral', questionId: 'lg_other_services', role: 'guardian' },
    { formKey: 'educationHistory', questionId: 'support_in_place', role: 'school_user' },
    { formKey: 'educationHistory', questionId: 'lg_support_in_place', role: 'guardian' },
    { formKey: 'theirMentalHealthHistory', questionId: 'current_support', role: 'guardian' },
    { formKey: 'theirFamilyAndHomeLife', questionId: 'support_used', role: 'guardian' },
  ],
  educationSupport: [
    { formKey: 'educationHistory', questionId: 'support_in_place', role: 'school_user' },
    { formKey: 'educationHistory', questionId: 'lg_support_in_place', role: 'guardian' },
  ],
};

const ROLE_LABELS = { guardian: 'Parent/carer', school_user: 'School' };

/** Find a question node (or group child) by id within a schema's sections. */
function findQuestion(schema, questionId) {
  for (const section of schema?.sections || []) {
    for (const q of section.questions || []) {
      if (q.questionId === questionId) return q;
      for (const child of q.children || []) {
        if (child.questionId === questionId) return child;
      }
    }
  }
  return null;
}

function optionLabel(question, optionId) {
  const opt = (question?.options || []).find((o) => o.optionId === optionId);
  return opt?.label || optionId;
}

/**
 * Human-readable display for one stored answer against its schema question.
 * Handles the shapes the RMS forms store: radio/select ({optionId,otherText}
 * or string), checkboxes ([{optionId,otherText}|string]), and free text.
 * `options` (when provided) restricts a checkbox to the factor-relevant ids.
 * Returns '' when there is nothing meaningful to show.
 */
/**
 * The individual display items for one stored answer: one entry per selected
 * checkbox option, a single entry for a radio/select or free-text answer, and
 * [] when there is nothing meaningful to show. Keeping the items un-joined lets
 * the UI render each as its own chip without guessing where to split a string.
 */
export function formatAnswerItems(question, answer, options) {
  if (answer === undefined || answer === null || answer === '') return [];
  const type = question?.type;

  if (type === 'checkboxes' || Array.isArray(answer)) {
    const items = Array.isArray(answer) ? answer : [answer];
    let selected = items
      .map((it) => (typeof it === 'object' ? it : { optionId: it }))
      .filter((it) => it.optionId);
    if (Array.isArray(options) && options.length) {
      selected = selected.filter((it) => options.includes(it.optionId));
    }
    return selected.map((it) => {
      const label = optionLabel(question, it.optionId);
      return it.otherText ? `${label}: ${it.otherText}` : label;
    });
  }

  if (type === 'radio' || type === 'select') {
    const optionId = typeof answer === 'object' ? answer.optionId : answer;
    if (!optionId) return [];
    const label = optionLabel(question, optionId);
    const otherText = typeof answer === 'object' ? answer.otherText : null;
    return [otherText ? `${label}: ${otherText}` : label];
  }

  // Free text / other primitive
  if (typeof answer === 'string') {
    const t = answer.trim();
    return t ? [t] : [];
  }
  if (typeof answer === 'object' && answer.otherText) {
    const t = String(answer.otherText).trim();
    return t ? [t] : [];
  }
  return [];
}

/**
 * The `itemSources` for one factor's entry in the decision's surfaced-factor
 * snapshot (TDI-698): the form and respondent role of each row, in row order,
 * so the letter can tag a school answer SENCO or Teacher by who submitted the
 * form. Spread into the entry; empty (no key) unless every row names both,
 * since the server needs one source per item.
 *
 * @param {Array<{formKey?: string, role?: string}>} rows
 * @returns {{itemSources?: Array<{formKey: string, role: string}>}}
 */
export function surfacedItemSources(rows) {
  if (!Array.isArray(rows) || !rows.length) return {};
  if (!rows.every((r) => r && r.formKey && (r.role === 'guardian' || r.role === 'school_user'))) {
    return {};
  }
  return { itemSources: rows.map((r) => ({ formKey: r.formKey, role: r.role })) };
}

/** The joined single-string form of {@link formatAnswerItems}. */
export function formatAnswerDisplay(question, answer, options) {
  return formatAnswerItems(question, answer, options).join(', ');
}

/**
 * Resolve the surfaced rows for every factor.
 *
 * @param {object} args
 * @param {Record<string, {answers?: object}>} args.formInstances - keyed `${formKey}_${role}`
 * @param {Record<string, object>} args.schemas - loaded form schemas keyed by formKey
 * @param {string|null} args.schoolPhase - 'primary' | 'secondary' (secondary skips HFP)
 * @param {Record<string, Array>} args.factorSources - factorKey -> sources map
 *   (ADHD_FACTOR_SOURCES by default; pass AUTISM_FACTOR_SOURCES for the autism wizard)
 * @returns {Record<string, Array<{label: string, value: string, values: string[], respondent: string,
 *   formKey: string, role: string}>>}
 *   factorKey -> rows (empty array when nothing to surface -> caller shows "Not captured")
 */
export function resolveOtherFactors({
  formInstances = {},
  schemas = {},
  schoolPhase = null,
  factorSources = ADHD_FACTOR_SOURCES,
} = {}) {
  const isSecondary = String(schoolPhase || '').toLowerCase() === 'secondary';
  const out = {};

  for (const [factorKey, sources] of Object.entries(factorSources)) {
    const rows = [];
    for (const src of sources) {
      // HFP sources exist only for primary-school referrals and need their
      // schema loaded; skip otherwise (factor falls back to its other sources).
      const isHfp = src.hfp || String(src.formKey).startsWith(HFP_FORM_PREFIX);
      if (isHfp && isSecondary) continue;
      const schema = schemas[src.formKey];
      if (!schema) continue; // schema not loaded (e.g. HFP on secondary) -> skip

      const instance = formInstances[`${src.formKey}_${src.role}`];
      const answers = instance?.answers;
      if (!answers) continue;

      // Whole-form HFP sources aren't itemised here (no per-question mapping);
      // only explicit questionId sources are surfaced.
      if (!src.questionId) continue;

      const question = findQuestion(schema, src.questionId);
      if (!question) continue;
      const values = formatAnswerItems(question, answers[src.questionId], src.options);
      if (!values.length) continue;

      rows.push({
        label: question.label || src.questionId,
        // Joined string kept for the decision snapshot / letter inputs; `values`
        // is the per-item list the UI renders as individual chips.
        value: values.join(', '),
        values,
        respondent: ROLE_LABELS[src.role] || src.role,
        // The form and respondent role the answer came from (TDI-698): the
        // letter tags a school answer SENCO or Teacher by who submitted it.
        formKey: src.formKey,
        role: src.role,
      });
    }
    out[factorKey] = rows;
  }
  return out;
}
